import { ABSENCE_TYPES, LEAVE_KINDS, type LeaveKind, type CalendarEntryDto, type ConflictDto, ENTRY_KINDS, type EntryKind, type MeDto, clampDateTo, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Button, Checkbox, Group, Modal, MultiSelect, SegmentedControl, Select, SimpleGrid, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { can } from '../../auth';
import { FilterChips } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';
import { AbsenceDeputyFields, deputyBody, deputyIncomplete, type DeputyValue, NO_DEPUTY } from './AbsenceDeputyFields';
import { ConflictList, usePeople, useTeams } from './common';

/**
 * Nowa nieobecność (własny wniosek; zatwierdzający — od razu zatwierdzona, także dla innych) z osobą zastępującą albo wydarzenie / wpis.
 * `initial` — z linku (np. „Ustaw zastępstwo” na wstrzymanej sprawie): pracownik, okres i numer sprawy.
 */
export function EntryDialog({
  me,
  kind: kind0,
  day,
  initial,
  onClose,
}: {
  me: MeDto;
  kind: EntryKind;
  day?: string;
  initial?: { userId?: string; from?: string; to?: string; caseNumber?: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const people = usePeople();
  const teams = useTeams();
  const approver = can(me, 'nieobecnosci.zatwierdzanie');
  const [kind, setKind] = useState<EntryKind>(kind0);
  const [f, setF] = useState({
    title: '',
    absenceType: 'urlop' as keyof typeof ABSENCE_TYPES,
    leaveKind: 'wypoczynkowy' as LeaveKind,
    userId: initial?.userId && (approver || initial.userId === me.id) ? initial.userId : me.id,
    userIds: [] as string[],
    teamIds: [] as string[],
    allUsers: false,
    dateFrom: initial?.from ?? day ?? todayInWarsaw(),
    dateTo: initial?.to && initial.to >= (initial.from ?? '') ? initial.to : (initial?.from ?? day ?? todayInWarsaw()),
    hourly: false,
    timeFrom: '08:00',
    timeTo: '12:00',
    note: '',
    attendanceRequired: false,
  });
  const [deputy, setDeputy] = useState<DeputyValue>(NO_DEPUTY);
  const [result, setResult] = useState<{ entry: CalendarEntryDto; conflicts: ConflictDto[] } | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api<{ entry: CalendarEntryDto; conflicts: ConflictDto[] }>('/calendar/entries', {
        method: 'POST',
        body: {
          kind,
          title: f.title,
          absenceType: kind === 'nieobecnosc' ? f.absenceType : null,
          leaveKind: kind === 'nieobecnosc' && f.absenceType === 'urlop' ? f.leaveKind : null,
          userIds: kind === 'nieobecnosc' ? [f.userId] : f.userIds,
          teamIds: kind === 'nieobecnosc' ? [] : f.teamIds,
          allUsers: kind !== 'nieobecnosc' && f.allUsers,
          dateFrom: f.dateFrom,
          dateTo: f.dateTo < f.dateFrom ? f.dateFrom : f.dateTo,
          timeFrom: f.hourly ? f.timeFrom : null,
          timeTo: f.hourly ? f.timeTo : null,
          note: f.note,
          attendanceRequired: kind === 'wydarzenie' && f.attendanceRequired,
          ...(kind === 'nieobecnosc' && !f.hourly ? deputyBody(deputy) : {}),
        },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      void qc.invalidateQueries({ queryKey: ['planning-board'] });
      void qc.invalidateQueries({ queryKey: ['absences-pending'] });
      void qc.invalidateQueries({ queryKey: ['process-inbox'] });
      notifyOk(r.entry.absenceStatus === 'wniosek' ? 'Złożono wniosek o nieobecność' : r.entry.absenceStatus === 'zatwierdzona' ? 'Dodano nieobecność' : 'Dodano wpis');
      if (r.conflicts.length) setResult(r);
      else onClose();
    },
    onError: notifyError,
  });
  const phone = useMediaQuery('(max-width: 48em)');
  const kinds = (Object.keys(ENTRY_KINDS) as EntryKind[]).filter((k) => k === 'nieobecnosc' || can(me, 'kalendarz.wpisy'));

  return (
    <Modal opened onClose={onClose} title={kind === 'nieobecnosc' ? 'Nowa nieobecność' : 'Nowy wpis w kalendarzu'} size="lg" fullScreen={phone}>
      {result ? (
        <Stack>
          <Alert color="orange" title="Wpis koliduje z zaplanowanymi zadaniami">
            <ConflictList conflicts={result.conflicts} />
            <Text size="sm" mt="xs">
              Zadania nie zostały zmienione — przeplanuj je w Planowaniu.
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button onClick={onClose}>Zamknij</Button>
          </Group>
        </Stack>
      ) : (
        <Stack>
          {kinds.length > 1 &&
            (phone ? (
              // na telefonie długie etykiety nie mieszczą się w przełączniku
              <Select label="Rodzaj wpisu" allowDeselect={false} value={kind} onChange={(v) => v && setKind(v as EntryKind)} data={kinds.map((k) => ({ value: k, label: ENTRY_KINDS[k] }))} />
            ) : (
              <FilterChips value={kind} onChange={setKind} options={kinds.map((k) => ({ value: k, label: ENTRY_KINDS[k] }))} />
            ))}
          {kind === 'nieobecnosc' ? (
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Select label="Rodzaj" data={Object.entries(ABSENCE_TYPES).map(([value, label]) => ({ value, label }))} value={f.absenceType} onChange={(v) => setF({ ...f, absenceType: (v ?? 'urlop') as typeof f.absenceType })} />
              {f.absenceType === 'urlop' && (
                <Select label="Rodzaj urlopu" data={Object.entries(LEAVE_KINDS).map(([value, label]) => ({ value, label }))} value={f.leaveKind} onChange={(v) => setF({ ...f, leaveKind: (v ?? 'wypoczynkowy') as LeaveKind })} />
              )}
              {approver ? (
                <Select label="Pracownik" searchable data={(people.data ?? []).map((p) => ({ value: p.id, label: p.fullName }))} value={f.userId} onChange={(v) => (setF({ ...f, userId: v ?? me.id }), setDeputy(NO_DEPUTY))} />
              ) : (
                <TextInput label="Pracownik" value={me.fullName} disabled />
              )}
            </SimpleGrid>
          ) : (
            <>
              <TextInput label="Tytuł" value={f.title} onChange={(e) => setF({ ...f, title: e.currentTarget.value })} data-autofocus />
              <Checkbox label="Wszyscy pracownicy" checked={f.allUsers} onChange={(e) => setF({ ...f, allUsers: e.currentTarget.checked })} />
              {!f.allUsers && (
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <MultiSelect label="Ekipy" data={(teams.data ?? []).map((t) => ({ value: t.id, label: t.name }))} value={f.teamIds} onChange={(v) => setF({ ...f, teamIds: v })} />
                  <MultiSelect label="Pracownicy" searchable data={(people.data ?? []).map((p) => ({ value: p.id, label: p.fullName }))} value={f.userIds} onChange={(v) => setF({ ...f, userIds: v })} />
                </SimpleGrid>
              )}
            </>
          )}
          <Group grow align="flex-end">
            <TextInput type="date" label="Od" value={f.dateFrom} onChange={(e) => setF({ ...f, dateFrom: e.currentTarget.value, dateTo: clampDateTo(e.currentTarget.value, f.dateTo) })} />
            <TextInput type="date" label="Do" value={f.dateTo} min={f.dateFrom || undefined} onChange={(e) => setF({ ...f, dateTo: e.currentTarget.value })} />
          </Group>
          <Group align="flex-end">
            {/* jak w oknie planu pracy: cały dzień albo godziny */}
            <SegmentedControl size="xs" value={f.hourly ? 'h' : 'd'} onChange={(v) => setF({ ...f, hourly: v === 'h' })} data={[{ value: 'd', label: 'Cały dzień' }, { value: 'h', label: 'Godziny' }]} mb={6} />
            {f.hourly && (
              <>
                <TextInput type="time" label="Od godz." value={f.timeFrom} onChange={(e) => setF({ ...f, timeFrom: e.currentTarget.value })} w={120} />
                <TextInput type="time" label="Do godz." value={f.timeTo} onChange={(e) => setF({ ...f, timeTo: e.currentTarget.value })} w={120} />
              </>
            )}
          </Group>
          <Textarea label="Uwagi" autosize minRows={1} value={f.note} onChange={(e) => setF({ ...f, note: e.currentTarget.value })} />
          {kind === 'nieobecnosc' && (
            <AbsenceDeputyFields
              me={me}
              personId={f.userId}
              value={deputy}
              onChange={setDeputy}
              hourly={f.hourly}
              // wpis zatwierdzającego za innego pracownika jest od razu zatwierdzony; własny — zwykle wniosek
              pending={!approver || f.userId === me.id}
              caseNumber={initial?.caseNumber}
            />
          )}
          {kind === 'wydarzenie' && (
            <Checkbox
              label="Potwierdzenie obecności"
              description="Uczestnicy potwierdzają obecność w aplikacji (Kadry → Wydarzenia firmowe); za osoby bez aplikacji obecność wpiszesz przy wydarzeniu w kalendarzu."
              checked={f.attendanceRequired}
              onChange={(e) => setF({ ...f, attendanceRequired: e.currentTarget.checked })}
            />
          )}
          {kind === 'nieobecnosc' && (!approver || f.userId === me.id) && (
            <Text size="xs" c="dimmed">
              Wniosek trafi kolejno do osób z drabinki akceptacji (karta pracownika → Akceptacja urlopów). Do czasu decyzji jest widoczny w planie jako ostrzeżenie. Wymiaru urlopu system nie sprawdza.
            </Text>
          )}
          {kind === 'nieobecnosc' && approver && f.userId !== me.id && (
            <Text size="xs" c="dimmed">
              Nieobecność innego pracownika wpisana przez Ciebie jest od razu zatwierdzona (np. zwolnienie zgłoszone telefonicznie).
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={kind === 'nieobecnosc' && deputyIncomplete(deputy)}>
              {kind === 'nieobecnosc' ? (approver && f.userId !== me.id ? 'Dodaj nieobecność' : 'Złóż wniosek') : 'Dodaj wpis'}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
